// MiniLogin.tsx
import { useState, useRef, useEffect } from "react";
import type { FormEvent } from "react";
import { useAuth } from "./AuthContext";
import { useNavigate } from "react-router-dom";
import styles from './MiniLogin.module.css'
import wankandoo from './img/wankandoo.png'
import ApiClient from "./utils/apiClient"
import type { ApiError } from "./utils/apiClient"
import { toast } from "react-toastify";
import { homePathForRole, roleLabel } from "./utils/roleHome";
import { isProtectedRoute } from "./utils/protectedRoutes";

interface MiniLoginProps {
    /** Zusätzliche Menüeinträge vor „Abmelden“ (mobil bei Impersonation: „Admin“). */
    extraItems?: Array<{ label: string; onClick: () => void }>;
}

export default function MiniLogin({ extraItems = [] }: MiniLoginProps) {
    const { isLoggedIn, login, logout, user, userRole } = useAuth();
    const [showMenu, setShowMenu] = useState(false);
    const [showLoginForm, setShowLoginForm] = useState(false);
    const [error, setError] = useState('');
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [showResendButton, setShowResendButton] = useState(false);

    const menuRef = useRef<HTMLDivElement>(null);
    const navigate = useNavigate();

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
                setShowMenu(false);
                setShowLoginForm(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    // Nach einem Login über das Popup zur Startseite der Rolle wechseln, sofern der
    // Nutzer gerade auf einer Seite ohne Rollenbezug steht (Startseite, Login, Hilfe …).
    const loggedInViaPopup = useRef(false);
    useEffect(() => {
        if (!isLoggedIn || !loggedInViaPopup.current) return;
        loggedInViaPopup.current = false;
        const home = homePathForRole(userRole);
        if (home && !isProtectedRoute(window.location.pathname)) {
            navigate(home);
        }
    }, [isLoggedIn, userRole, navigate]);

    const handleAvatarClick = () => {
        if (isLoggedIn) {
            setShowMenu(!showMenu);
            setShowLoginForm(false);
        } else {
            setShowLoginForm(!showLoginForm);
            setShowMenu(false);
        }
    };

    const handleLogin = async (e: FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        setError('');
        setShowResendButton(false);

        try {
            // login() wirft nicht, sondern liefert {success, message}
            const result = await login(email.toLowerCase().trim(), password);
            if (!result.success) {
                throw new Error(result.message || 'Ein Fehler ist aufgetreten');
            }
            loggedInViaPopup.current = true;
            setShowLoginForm(false);
            setEmail('');
            setPassword('');
            toast.success("Login erfolgreich");
        } catch (caught) {
            const err = caught as ApiError;
            // Benutzerfreundliche Fehlermeldungen
            switch (err.message) {
                case "Invalid credentials":
                    setError('E-Mail oder Passwort ist falsch');
                    break;
                case "User not found":
                    setError('Kein Account mit dieser E-Mail gefunden');
                    break;
                case "Email not confirmed":
                    setError('Bitte bestätige zuerst deine E-Mail-Adresse');
                    setShowResendButton(true);
                    break;
                default:
                    setError(err.message || 'Ein Fehler ist aufgetreten');
            }
        }
    };

    const handleLogout = async () => {
        try {
            const result = await logout();
            if (result.success) {
                setShowMenu(false);
                navigate('/');
            }
        } catch (err) {
            setError('Fehler beim Ausloggen');
        }
    };

    const handleRegisterClick = () => {
        setShowLoginForm(false);
        navigate('/register');
    };

    return (
        <div ref={menuRef} className={styles.miniLoginWrapper}>
            {/* Avatar Button */}
            <button
                className={`${styles.avatarButton} ${
                    userRole === 'teacher' ? styles.teacher :
                    userRole === 'student' ? styles.student : ''
                }`}
                onClick={handleAvatarClick}
            >
                <img
                    src={(user?.profilePicture as string | undefined) || wankandoo}
                    alt="Avatar"
                    className={styles.avatarImage}
                />
            </button>

            {/* Dropdown Menu für eingeloggte User */}
            {showMenu && isLoggedIn && (
                <div className={styles.dropdownMenu}>
                    <div className={styles.userInfo}>
                        <img
                            src={(user?.profilePicture as string | undefined) || wankandoo}
                            alt="Avatar"
                            className={styles.menuAvatar}
                        />
                        <div className={styles.userDetails}>
                            <span className={styles.userName}>{user?.username}</span>
                            <span className={styles.userEmail}>{user?.email}</span>
                            <span className={`${styles.userRole} ${userRole === 'teacher' ? styles.teacher : styles.student}`}>{roleLabel(userRole)}</span>
                        </div>
                    </div>



                    <div className={styles.menuDivider} />

                    <div className={styles.menuItems}>
                        <button onClick={() => { setShowMenu(false); navigate('/profile'); }} className={styles.menuItem}>
                            Profil ansehen
                        </button>
                        <button onClick={() => { setShowMenu(false); navigate('/contact'); }} className={styles.menuItem}>
                            Kontakt
                        </button>
                        {extraItems.map(item => (
                            <button key={item.label} onClick={() => { setShowMenu(false); item.onClick(); }} className={styles.menuItem}>
                                {item.label}
                            </button>
                        ))}
                        <button onClick={handleLogout} className={styles.menuItem}>
                            Abmelden
                        </button>
                    </div>
                </div>
            )}

            {/* Login Form */}
            {showLoginForm && !isLoggedIn && (
                <div className={styles.loginPopup}>
                    <form onSubmit={handleLogin}>
                        <h3>Login</h3>
                        <div className={styles.inputGroup}>
                            <input
                                type="email"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                placeholder="E-Mail"
                                required
                                autoComplete="email"
                            />
                        </div>
                        <div className={styles.inputGroup}>
                            <input
                                type="password"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                placeholder="Passwort"
                                required
                                autoComplete="current-password"
                            />
                        </div>
                        {error && <div className={styles.error}>{error}</div>}
                        {showResendButton && (
                            <div className={styles.resendConfirmation}>
                                <button
                                    type="button"
                                    onClick={async () => {
                                        try {
                                            await ApiClient.post('auth/resend-confirmation', { email: email, password: password });
                                            setError('Bestätigungs-Link wurde erneut gesendet');
                                            setShowResendButton(false);
                                        } catch (caught) {
                                            const err = caught as ApiError;
                                            setError(err.originalErrorData?.detail || err.message || 'Fehler beim Senden');
                                        }
                                    }}
                                    className={styles.resendButton}
                                >
                                    Bestätigungs-Link erneut senden
                                </button>
                            </div>
                        )}
                        <button type="submit" className={styles.loginButton}>
                            Anmelden
                        </button>
                        <div className={styles.registerLink}>
                            Noch kein Account?
                            <button onClick={handleRegisterClick}>
                                Registrieren
                            </button>
                        </div>
                    </form>
                </div>
            )}
        </div>
    );
}
